
import React, { useState } from 'react';
import { SEOMetaTags } from '@/components/SEOMetaTags';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';

export default function WaitlistPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast({
        title: 'Ungültige E-Mail-Adresse',
        description: 'Bitte gib eine gültige E-Mail-Adresse ein.',
        variant: 'destructive'
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Use type assertion to fix the TypeScript error
      const { error } = await supabase
        .from('studio_interest_log')
        .insert({
          user_id: null, // Anonymous submission
          studio_id: 'waitlist', 
          invited: false,
          notes: email, // Store email in notes
        } as any);
        
      if (error) throw error;
      
      setIsSuccess(true);
      setEmail('');
      
      toast({
        title: 'Erfolgreich eingetragen!',
        description: 'Du wirst benachrichtigt, sobald CHRONA verfügbar ist.',
      });
    } catch (error) {
      console.error('Error submitting to waitlist:', error);
      toast({
        title: 'Etwas ist schief gelaufen',
        description: 'Bitte versuche es später noch einmal.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-[#212121] flex flex-col items-center justify-center p-4">
      <SEOMetaTags 
        title="CHRONA - Jetzt für Early Access anmelden"
        description="Melde dich für die Warteliste an und sei einer der ersten, der CHRONA nutzen kann - die ultimative Terminbuchungsplattform für deine Schönheits- und Wellness-Dienstleistungen."
        noindex={true} // This is a waitlist page, we don't want it indexed yet
      />
      
      <div className="container max-w-screen-md mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-[#F8F8F8] mb-4">
            CHRONA <span className="text-[#2C6E49]">kommt bald</span>
          </h1>
          <p className="text-xl text-[#F8F8F8]/80 max-w-2xl mx-auto">
            Die ultimative Plattform für die Buchung von Beauty- und Wellness-Terminen
            wird in Kürze verfügbar sein. Melde dich an, um zu den Ersten zu gehören.
          </p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="bg-[#1A1A1A] border-[#F8F8F8]/10">
            <CardHeader>
              <CardTitle className="text-[#F8F8F8]">Auf die Warteliste</CardTitle>
              <CardDescription className="text-[#F8F8F8]/60">
                Erhalte Zugang zur Beta-Version und exklusive Vorteile
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isSuccess ? (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#2C6E49]/20 mb-4">
                    <svg className="w-8 h-8 text-[#2C6E49]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-[#F8F8F8]">Vielen Dank für dein Interesse!</h3>
                  <p className="mt-2 text-[#F8F8F8]/60">
                    Wir haben dich auf unsere Warteliste gesetzt und werden dich benachrichtigen, 
                    sobald wir starten.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      type="email"
                      placeholder="deine@email.de"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-[#212121] border-[#F8F8F8]/20"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#2C6E49] hover:bg-[#4C956C]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Wird eingetragen...' : 'Auf die Warteliste setzen'}
                  </Button>
                </form>
              )}
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 border-t border-[#F8F8F8]/10 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                <div className="flex flex-col items-center p-3 rounded-md bg-[#212121]">
                  <div className="text-2xl text-[#2C6E49] font-bold">1.</div>
                  <div className="text-sm text-[#F8F8F8]/70 text-center">Frühzeitiger Zugang</div>
                </div>
                <div className="flex flex-col items-center p-3 rounded-md bg-[#212121]">
                  <div className="text-2xl text-[#2C6E49] font-bold">2.</div>
                  <div className="text-sm text-[#F8F8F8]/70 text-center">Exklusive Angebote</div>
                </div>
                <div className="flex flex-col items-center p-3 rounded-md bg-[#212121]">
                  <div className="text-2xl text-[#2C6E49] font-bold">3.</div>
                  <div className="text-sm text-[#F8F8F8]/70 text-center">Persönliche Betreuung</div>
                </div>
              </div>
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
